import React from 'react';
import ErrorFallback from '../../../../common/ErrorFallback';
import RenderDetails, { DetailItem } from '../../../../common/RenderDetails';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Empty, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
export interface BotDetailsComponentProps {
  data: DetailItem[];
  success: boolean;
  isLoading: boolean;
  error: Error | null;
}

const BotDetailsComponent = ({ data, success, isLoading, error }: BotDetailsComponentProps) => {
  const { t } = useTranslation();

  if (!data && !isLoading) return <Empty description={t('common.details.noData')} />;

  if (error)
    return (
      <ErrorFallback
        details={t('common.details.errorLoadingData')}
        icon={<ExclamationCircleOutlined />}
        error={error}
        resetErrorBoundary={() => {}}
      />
    );

  return (
    <Spin spinning={isLoading} tip={t('common.loading')}>
      <RenderDetails data={data} columns={2} isLoading={isLoading} />
    </Spin>
  );
};

// Custom comparison function for memoization
const areEqual = (
  prevProps: BotDetailsComponentProps,
  nextProps: BotDetailsComponentProps
): boolean => {
  // Compare primitive props
  if (
    prevProps.isLoading !== nextProps.isLoading ||
    prevProps.success !== nextProps.success ||
    prevProps.error !== nextProps.error
  ) {
    return false;
  }

  // Compare data arrays
  if (prevProps.data.length !== nextProps.data.length) {
    return false;
  }

  // Compare each item in the data array
  return prevProps.data.every((item, index) => {
    const nextItem = nextProps.data[index];
    return (
      item.label === nextItem.label &&
      // For React nodes, we can only do a shallow comparison
      // This is safe because React will handle re-rendering if the actual content changes
      item.value === nextItem.value
    );
  });
};

// Memoize the component with custom comparison
export default React.memo(BotDetailsComponent, areEqual);
